/**
 * Customer / supplier group admin — `/settings/partner-groups` (SPEC screen 6).
 * Two tabs over two symmetric contract resources; each group carries the AR
 * (customer) or AP (supplier) control account.
 *
 * The account field is a validated account-id input rather than a rich
 * account picker: the partners contract exposes no account lookup, and the
 * ledger's `GET /accounts` belongs to another lane's transport. The server's
 * `account_not_postable` / `account_inactive` refusals land inline on the
 * field, so an unpostable id is caught where it was typed.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@erp/ui";

import { DataTable, badgeCell, textCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  archiveCustomerGroup,
  archiveSupplierGroup,
  createCustomerGroup,
  createSupplierGroup,
  getCustomerGroup,
  getSupplierGroup,
  updateCustomerGroup,
  updateSupplierGroup,
  type CustomerGroup,
  type LifecycleStatus,
  type SupplierGroup,
} from "../api";
import { partnersKeys, useCustomerGroups, useSupplierGroups } from "../queries";

const CODE_RE = /^[a-z][a-z0-9_]*$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ACCOUNT_PROBLEM_CODES = new Set(["account_not_postable", "account_inactive"]);

/** The two group resources differ only in labels and transport functions. */
type AnyGroup = {
  id: string;
  code: string;
  name: string;
  description: string;
  account_id?: string;
  status: LifecycleStatus;
};

interface GroupResource {
  kind: "customer" | "supplier";
  queryKey: QueryKey;
  accountLabel: string;
  newLabel: string;
  emptyLabel: string;
  createTitle: string;
  editTitle: string;
  get: (id: string) => Promise<{ data: AnyGroup; etag: string | null }>;
  create: (input: GroupInput, key: string) => Promise<AnyGroup>;
  update: (
    id: string,
    patch: GroupPatch,
    ifMatch: string,
  ) => Promise<{ data: AnyGroup; etag: string | null }>;
  archive: (id: string) => Promise<void>;
}

interface GroupInput {
  code: string;
  name: string;
  description: string;
  account_id?: string;
}

interface GroupPatch {
  name?: string;
  description?: string;
  account_id?: string | null;
  status?: LifecycleStatus;
}

interface GroupDraft {
  code: string;
  name: string;
  description: string;
  accountId: string;
}

const EMPTY_DRAFT: GroupDraft = { code: "", name: "", description: "", accountId: "" };

export interface PartnerGroupsPageProps {
  canReadCustomer?: boolean;
  canCreateCustomer?: boolean;
  canUpdateCustomer?: boolean;
  canArchiveCustomer?: boolean;
  canReadSupplier?: boolean;
  canCreateSupplier?: boolean;
  canUpdateSupplier?: boolean;
  canArchiveSupplier?: boolean;
  initialTab?: "customer" | "supplier";
}

export function PartnerGroupsPage({
  canReadCustomer = true,
  canCreateCustomer = true,
  canUpdateCustomer = true,
  canArchiveCustomer = true,
  canReadSupplier = true,
  canCreateSupplier = true,
  canUpdateSupplier = true,
  canArchiveSupplier = true,
  initialTab,
}: PartnerGroupsPageProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"customer" | "supplier">(
    initialTab ?? (canReadCustomer ? "customer" : "supplier"),
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">{t("partners.groups.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("partners.groups.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as "customer" | "supplier")}>
        <TabsList>
          <TabsTrigger value="customer" disabled={!canReadCustomer}>
            {t("partners.groups.customerTab")}
          </TabsTrigger>
          <TabsTrigger value="supplier" disabled={!canReadSupplier}>
            {t("partners.groups.supplierTab")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="customer">
          {canReadCustomer ? (
            <GroupTable
              kind="customer"
              canCreate={canCreateCustomer}
              canUpdate={canUpdateCustomer}
              canArchive={canArchiveCustomer}
            />
          ) : (
            <p className="pt-4 text-sm text-muted-foreground">
              {t("errors.forbidden", { permission: "partners.customer_group.read" })}
            </p>
          )}
        </TabsContent>
        <TabsContent value="supplier">
          {canReadSupplier ? (
            <GroupTable
              kind="supplier"
              canCreate={canCreateSupplier}
              canUpdate={canUpdateSupplier}
              canArchive={canArchiveSupplier}
            />
          ) : (
            <p className="pt-4 text-sm text-muted-foreground">
              {t("errors.forbidden", { permission: "partners.supplier_group.read" })}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GroupTable({
  kind,
  canCreate,
  canUpdate,
  canArchive,
}: {
  kind: "customer" | "supplier";
  canCreate: boolean;
  canUpdate: boolean;
  canArchive: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const customerQuery = useCustomerGroups(kind === "customer");
  const supplierQuery = useSupplierGroups(kind === "supplier");
  const listQuery = kind === "customer" ? customerQuery : supplierQuery;
  const groups: AnyGroup[] = (listQuery.data ?? []) as AnyGroup[];

  const resource: GroupResource = useMemo(
    () =>
      kind === "customer"
        ? {
            kind,
            queryKey: partnersKeys.customerGroups.all,
            accountLabel: t("partners.groups.customerAccount"),
            newLabel: t("partners.groups.newCustomer"),
            emptyLabel: t("partners.groups.emptyCustomer"),
            createTitle: t("partners.groups.createCustomerTitle"),
            editTitle: t("partners.groups.editCustomerTitle"),
            get: (id) => getCustomerGroup(id) as Promise<{ data: AnyGroup; etag: string | null }>,
            create: (input, key) =>
              createCustomerGroup(input, key) as Promise<CustomerGroup> as Promise<AnyGroup>,
            update: (id, patch, ifMatch) =>
              updateCustomerGroup(id, patch, ifMatch) as Promise<{
                data: AnyGroup;
                etag: string | null;
              }>,
            archive: archiveCustomerGroup,
          }
        : {
            kind,
            queryKey: partnersKeys.supplierGroups.all,
            accountLabel: t("partners.groups.supplierAccount"),
            newLabel: t("partners.groups.newSupplier"),
            emptyLabel: t("partners.groups.emptySupplier"),
            createTitle: t("partners.groups.createSupplierTitle"),
            editTitle: t("partners.groups.editSupplierTitle"),
            get: (id) => getSupplierGroup(id) as Promise<{ data: AnyGroup; etag: string | null }>,
            create: (input, key) =>
              createSupplierGroup(input, key) as Promise<SupplierGroup> as Promise<AnyGroup>,
            update: (id, patch, ifMatch) =>
              updateSupplierGroup(id, patch, ifMatch) as Promise<{
                data: AnyGroup;
                etag: string | null;
              }>,
            archive: archiveSupplierGroup,
          },
    [kind, t],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<GroupDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Partial<Record<keyof GroupDraft, string>>>({});
  const [archiveTarget, setArchiveTarget] = useState<AnyGroup | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: resource.queryKey });
  };

  const applyError = (error: unknown): void => {
    if (error instanceof ApiError && error.code !== undefined) {
      if (ACCOUNT_PROBLEM_CODES.has(error.code)) {
        setErrors({ accountId: error.problem?.detail ?? t("partners.groups.accountInvalid") });
        return;
      }
      if (error.status === 409) {
        setErrors({ code: error.problem?.detail ?? t("partners.groups.codeRequired") });
        return;
      }
    }
    surfaceProblem(error, t);
  };

  const loadForEdit = useMutation({
    mutationFn: (groupId: string) => resource.get(groupId),
    onSuccess: ({ data, etag }) => {
      setEditing({ id: data.id, etag });
      setDraft({
        code: data.code,
        name: data.name,
        description: data.description,
        accountId: data.account_id ?? "",
      });
      setErrors({});
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createMutation = useIdempotentMutation<AnyGroup, GroupInput>({
    mutationFn: (input, key) => resource.create(input, key),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: applyError,
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: GroupPatch; ifMatch: string }) =>
      resource.update(input.id, input.patch, input.ifMatch),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: applyError,
  });

  const archiveMutation = useMutation({
    mutationFn: (groupId: string) => resource.archive(groupId),
    onSuccess: () => {
      setArchiveTarget(null);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const restoreMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const { etag } = await resource.get(groupId);
      if (etag === null) throw new ApiError("Missing group ETag; reload and retry", 428);
      return resource.update(groupId, { status: "active" }, etag);
    },
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const set = <K extends keyof GroupDraft>(key: K, value: GroupDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = (): void => {
    const next: Partial<Record<keyof GroupDraft, string>> = {};
    if (editing === null && !CODE_RE.test(draft.code.trim())) {
      next.code = t("partners.groups.codeRequired");
    }
    if (draft.name.trim() === "") next.name = t("partners.groups.nameRequired");
    const accountId = draft.accountId.trim();
    if (accountId !== "" && !UUID_RE.test(accountId)) {
      next.accountId = t("partners.groups.accountInvalid");
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    if (editing === null) {
      createMutation.mutate({
        code: draft.code.trim(),
        name: draft.name.trim(),
        description: draft.description.trim(),
        ...(accountId === "" ? {} : { account_id: accountId }),
      });
      return;
    }
    if (editing.etag === null) {
      setErrors({ name: t("partners.groups.nameRequired") });
      return;
    }
    updateMutation.mutate({
      id: editing.id,
      ifMatch: editing.etag,
      patch: {
        name: draft.name.trim(),
        description: draft.description.trim(),
        // Merge-patch: an emptied account must be nulled, not omitted.
        account_id: accountId === "" ? null : accountId,
      },
    });
  };

  const columns = useMemo<ColumnDef<AnyGroup>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("partners.groups.columns.code"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("partners.groups.columns.name"),
        enableSorting: false,
        cell: (ctx) => <span dir="auto">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "description",
        id: "description",
        header: t("partners.groups.columns.description"),
        enableSorting: false,
        cell: (ctx) => <span dir="auto">{textCell(ctx.getValue<string>())}</span>,
      },
      {
        accessorKey: "account_id",
        id: "account_id",
        header: t("partners.groups.columns.account"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          if (value === undefined) {
            return <span className="text-muted-foreground">{t("partners.groups.noAccount")}</span>;
          }
          return (
            <span dir="ltr" className="font-mono text-xs">
              {value}
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("partners.groups.columns.status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<LifecycleStatus>();
          return badgeCell(
            value,
            { active: "success", archived: "secondary" },
            t(`partners.status.${value}`),
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: (ctx) => {
          const group = ctx.row.original;
          return (
            <span className="flex flex-wrap items-center justify-end gap-1">
              {canUpdate && group.status === "active" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={loadForEdit.isPending}
                  onClick={() => loadForEdit.mutate(group.id)}
                >
                  {resource.editTitle}
                </Button>
              ) : null}
              {canArchive && group.status === "active" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setArchiveTarget(group)}
                >
                  {t("partners.common.archive")}
                </Button>
              ) : null}
              {canUpdate && group.status === "archived" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={restoreMutation.isPending}
                  onClick={() => restoreMutation.mutate(group.id)}
                >
                  {t("partners.common.restore")}
                </Button>
              ) : null}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row actions derive from t and the mutation handles
    [t, resource, canUpdate, canArchive, loadForEdit.isPending, restoreMutation.isPending],
  );

  const fieldError = (key: keyof GroupDraft) =>
    errors[key] === undefined ? null : (
      <p role="alert" className="text-sm text-destructive">
        {errors[key]}
      </p>
    );

  return (
    <div className="flex flex-col gap-3 pt-3">
      {canCreate ? (
        <div>
          <Button
            type="button"
            onClick={() => {
              setEditing(null);
              setDraft(EMPTY_DRAFT);
              setErrors({});
              setDialogOpen(true);
            }}
          >
            {resource.newLabel}
          </Button>
        </div>
      ) : null}

      {listQuery.isError ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          <span>{t("partners.groups.loadError")}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void listQuery.refetch()}
          >
            {t("actions.retry")}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={groups}
          getRowId={(row) => row.id}
          showColumnsMenu={false}
          emptyMessage={resource.emptyLabel}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>
              {editing === null ? resource.createTitle : resource.editTitle}
            </DialogTitle>
            <DialogDescription>{t("partners.groups.accountHint")}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`group-code-${kind}`}>{t("partners.groups.code")}</Label>
              <Input
                id={`group-code-${kind}`}
                dir="ltr"
                autoComplete="off"
                value={draft.code}
                disabled={editing !== null}
                aria-invalid={errors.code !== undefined}
                onChange={(event) => set("code", event.target.value.toLowerCase())}
              />
              <p className="text-xs text-muted-foreground">{t("partners.groups.codeHint")}</p>
              {fieldError("code")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`group-name-${kind}`}>{t("partners.groups.name")}</Label>
              <Input
                id={`group-name-${kind}`}
                dir="auto"
                value={draft.name}
                aria-invalid={errors.name !== undefined}
                onChange={(event) => set("name", event.target.value)}
              />
              {fieldError("name")}
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor={`group-description-${kind}`}>
                {t("partners.groups.description")}
              </Label>
              <Input
                id={`group-description-${kind}`}
                dir="auto"
                value={draft.description}
                onChange={(event) => set("description", event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor={`group-account-${kind}`}>{resource.accountLabel}</Label>
              <Input
                id={`group-account-${kind}`}
                dir="ltr"
                autoComplete="off"
                placeholder={t("partners.groups.accountPlaceholder")}
                value={draft.accountId}
                aria-invalid={errors.accountId !== undefined}
                onChange={(event) => set("accountId", event.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t("partners.groups.accountHint")}</p>
              {fieldError("accountId")}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={submit}
            >
              {editing === null ? t("partners.groups.create") : t("partners.groups.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveTarget !== null} onOpenChange={() => setArchiveTarget(null)}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("partners.groups.archiveTitle")}</DialogTitle>
            <DialogDescription>{t("partners.groups.archiveBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveTarget(null)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={archiveMutation.isPending}
              onClick={() => {
                if (archiveTarget !== null) archiveMutation.mutate(archiveTarget.id);
              }}
            >
              {t("partners.common.archive")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
